import { ReactElement, useContext, useEffect } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { LoginCallback, useOktaAuth } from "@okta/okta-react";
import {
  setAppSession,
  hasValidReauthTime,
  hasReauthState,
} from "lib/appSessionManager";

import PageLoading from "global-components/pageLoading/PageLoading";
import { PATH_TO_HOME, PATH_TO_LOGIN_CALLBACK } from "constants/navigation";
import { SUCCESSFUL_STEP_UP_AUTH } from "../../constants/pageAlertsCopy";
import PageAlertContext from "../../contexts/PageAlertContext";

const CustomLoginCallback = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const { authState } = useOktaAuth();
  const { setAlert } = useContext(PageAlertContext);
  const navigate = useNavigate();

  useEffect(() => {
    const urlPath = window.location.pathname;
    if (
      // During auth flow, the Okta "LoginCallback" component clears all query parameters from the url and updates the browser's history state to the url without them.
      // If a user were to navigate back, they are taken to /login/callback. The Okta "LoginCallback" component does not account for handling a url without the query parameter "code".
      // It will silently return and perpetually show the provided "loadingElement" prop.
      // In this scenario, instead of rendering "LoginCallback", redirect the user back to the home page.
      urlPath === PATH_TO_LOGIN_CALLBACK &&
      !searchParams.get("code")
    ) {
      navigate(PATH_TO_HOME);
    }
  }, [navigate, searchParams]);

  const isValidReauth =
    hasReauthState(searchParams) &&
    authState &&
    hasValidReauthTime(searchParams, authState);
  useEffect(() => {
    // If the user has successfully re-authenticated, display a success alert.
    // This must be called in the `useEffect` hook because setAlert call changes state.
    // The hook isolates that change to a side effect, which allows the PageAlertProvider to
    // render separate from the rendering of the CustomLoginCallback component.
    if (isValidReauth) {
      setAlert({
        message: SUCCESSFUL_STEP_UP_AUTH,
        color: "additive",
      });
    }
  }, [isValidReauth, setAlert]);

  if (isValidReauth) {
    setAppSession(authState);
  }

  return (
    <LoginCallback
      // Okta LoginCallback component will throw an error if the provided "code" query parameter is invalid.
      // In this scenario, redirect the user to the home page, which will force them to re-auth with Okta.
      errorComponent={() => {
        return <Navigate to={PATH_TO_HOME} />;
      }}
      loadingElement={<PageLoading />}
    />
  );
};
export default CustomLoginCallback;
